import { useEffect, useMemo, useRef, useState } from "react";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import pickJevItemSuggestions from "~/helpers/search/pickJevItemSuggestions";
import useDebounce from "~/hooks/useDebounce";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import { type ItemSearchMatch, suggestItemSearchMatches } from "~/services/aiOrchestrator";

const FEW_BUILT_IN_RESULTS = 3;
const PAUSE_MS = 500;

interface UseJevItemSuggestionsArgs {
	search: string;
	shownItemIds: readonly string[];
	skip: boolean;
}

interface JevAnswer {
	matches: ItemSearchMatch[];
	text: string;
}

const useJevItemSuggestions = ({ search, shownItemIds, skip }: UseJevItemSuggestionsArgs): ItemSearchMatch[] => {
	const { isEnabled } = useFeatureFlag(FeatureFlagName.JevDecisions);
	const text = search.trim();
	const pausedText = useDebounce(text, PAUSE_MS);
	const askedTextRef = useRef("");
	const [answer, setAnswer] = useState<JevAnswer | null>(null);
	const canSuggest = isEnabled && shownItemIds.length < FEW_BUILT_IN_RESULTS;
	const shouldAsk = canSuggest && !skip && text !== "" && pausedText === text;

	useEffect(() => {
		if (!shouldAsk || askedTextRef.current === text) return;
		askedTextRef.current = text;
		void suggestItemSearchMatches(text).then((matches) => {
			if (askedTextRef.current === text) setAnswer({ matches, text });
		});
	}, [shouldAsk, text]);

	return useMemo(
		() => (canSuggest && answer?.text === text ? pickJevItemSuggestions(answer.matches, shownItemIds) : []),
		[answer, canSuggest, shownItemIds, text]
	);
};

export default useJevItemSuggestions;
